// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const ExecutionQueueView = ({ onNavigate = undefined }: { onNavigate?: any }) => {
  const [queue, setQueue] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState<any>(null);
  const [selected, setSelected] = useState(new Set());
  const { selected: clientScopeSelected } = useClientScope();

  const loadQueue = async () => {
    setLoading(true);
    const data = await apiCall("/esign/execution-queue");
    if (data?.queue) setQueue(data.queue);
    setSelected(new Set());
    setLoading(false);
  };

  useEffect(() => { loadQueue(); }, [clientScopeSelected]);

  const toggleSelect = (id: string) => { setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }); };
  const toggleAll = () => { if (selected.size === queue.length) setSelected(new Set()); else setSelected(new Set(queue.map(q => q.id))); };

  const importToRepo = async (envelopeId: string) => {
    if (!window.confirm("Send this signed document to AI Import for extraction and review?")) return;
    setImporting(envelopeId);
    const r = await apiCall("/esign/import-to-repository", { method: "POST", body: JSON.stringify({ envelopeId }) });
    setImporting(null);
    if (r?.batchId) {
      setQueue(prev => prev.filter((q: any) => q.id !== envelopeId));
      setSelected(prev => { const next = new Set(prev); next.delete(envelopeId); return next; });
      if (onNavigate) onNavigate('import');
    } else {
      alert(r?.error || "Import failed");
    }
  };

  const bulkImport = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Send ${selected.size} document(s) to AI Import for extraction and review?`)) return;
    setImporting('bulk');
    const envelopeIds = [...selected];
    let success = 0;
    const errors = [];
    const token = localStorage.getItem("kontractai_token");
    for (let i = 0; i < envelopeIds.length; i++) {
      const envelopeId = envelopeIds[i];
      try {
        const res = await fetch("/api/esign/import-to-repository", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ envelopeId }),
        });
        const r = await res.json().catch((): any => null);
        if (res.ok && r?.batchId) {
          success++;
        } else {
          const msg = r?.error || `HTTP ${res.status}`;
          errors.push(msg);
          console.error(`[BulkImport] ${envelopeId}: ${msg}`);
        }
      } catch (err: any) {
        errors.push(err.message);
        console.error(`[BulkImport] ${envelopeId} failed:`, err);
      }
      // Delay between imports to stagger extraction workers and avoid rate limits
      if (i < envelopeIds.length - 1) await new Promise(res => setTimeout(res, 1000));
    }
    setImporting(null);
    setSelected(new Set());
    if (success > 0) {
      if (errors.length > 0) alert(`${success} queued successfully, ${errors.length} failed:\n${errors.join('\n')}`);
      loadQueue();
      if (onNavigate) onNavigate('import');
    } else {
      alert(`No documents were queued. Errors:\n${errors.join('\n')}`);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.txM }}>Loading execution queue...</div>;

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "32px 40px", maxWidth: 960, margin: "0 auto" }}>
      <SectionTitle sub={`${queue.length} document${queue.length !== 1 ? "s" : ""} awaiting import`} right={<>
        {queue.length > 0 && <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.txD, cursor: "pointer" }}><input type="checkbox" checked={queue.length > 0 && selected.size === queue.length} onChange={toggleAll} /> Select All</label>}
        {selected.size > 0 && <Btn v="primary" sz="sm" disabled={importing === 'bulk'} onClick={() => { bulkImport(); }} style={{ backgroundColor: T.grn }}>{importing === 'bulk' ? 'Sending...' : `Send ${selected.size} to AI Import`}</Btn>}
        <RefreshBtn onClick={() => { loadQueue(); }} />
      </>}>Execution Queue</SectionTitle>
      {queue.length === 0 ? (
        <Card style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }}>✍</div>
          <div style={{ fontSize: 14, color: T.txM, fontWeight: 500 }}>No signed documents pending import</div>
          <div style={{ fontSize: 12, color: T.txD, marginTop: 6 }}>Completed DocuSign envelopes will appear here for review and import into the contract repository.</div>
        </Card>
      ) : queue.map(item => {
        const signers = typeof item.signers === "string" ? JSON.parse(item.signers) : (item.signers || []);
        return (
          <Card key={item.id} style={{ marginBottom: 12, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)} style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer" }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.tx, marginBottom: 4 }}>{item.request_title || item.envelope_id}</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {item.counterparty && <span style={{ fontSize: 11, color: T.txM }}>Counterparty: <strong style={{ color: T.tx }}>{item.counterparty}</strong></span>}
                  {item.request_type && <span style={{ fontSize: 11, color: T.txM }}>Type: <strong style={{ color: T.tx }}>{item.request_type}</strong></span>}
                  {item.company_entity && <span style={{ fontSize: 11, color: T.txM }}>Entity: <strong style={{ color: T.tx }}>{item.company_entity}</strong></span>}
                </div>
              </div>
              </div>
              <Badge c={T.grn} b={T.grnS}>Completed</Badge>
            </div>
            <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: 11, color: T.txD }}>
              {item.sent_by_name && <span>Sent by: {item.sent_by_name}</span>}
              {item.sent_at && <span>Sent: {fmtDate(item.sent_at)}</span>}
              {item.completed_at && <span>Completed: {fmtDate(item.completed_at)}</span>}
            </div>
            {signers.length > 0 && (
              <div style={{ marginBottom: 10, padding: "8px 12px", backgroundColor: T.bg3, borderRadius: 6, border: `1px solid ${T.brd}` }}>
                <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Signers</div>
                {signers.map((s: any, i: number) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: T.tx, marginBottom: 2 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: T.grn, display: "inline-block" }} />
                    <span style={{ fontWeight: 500 }}>{s.name}</span>
                    <span style={{ color: T.txD }}>{s.email}</span>
                    {s.signedDateTime && <span style={{ marginLeft: "auto", fontSize: 10, color: T.txD }}>Signed {fmtDate(s.signedDateTime)}</span>}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {item.completed_document_path && <Btn v="secondary" sz="sm" onClick={() => window.open(`/api/files/view?path=${encodeURIComponent(item.completed_document_path)}`, "_blank")}>View PDF</Btn>}
              <Btn v="primary" sz="sm" disabled={importing === item.id} onClick={() => importToRepo(item.id)} style={{ backgroundColor: T.grn, borderColor: T.grn }}>{importing === item.id ? "Sending..." : "Send to AI Import"}</Btn>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

/* ═══════════════════ NOTIFICATIONS ═══════════════════ */
